import React, { useCallback, useEffect, useRef } from 'react'
import moment from 'moment-timezone'
import { xtractAPI } from 'actions/apiActions'
import { useDispatch, useSelector } from 'react-redux'
import globalConfig from 'Helper/configHelpers'
import { scanForProblems } from 'accessibilityjs'
import { updateLog } from 'actions/logActions'
import { closeModal, openModal } from 'actions/modalActions'
import _ from 'lodash'
import { getHardwareScreenType } from '../../Helper/screenTypeHelpers'

const { heap_id: heapId } = globalConfig

const XtractLogger = (props) => {
  const dispatch = useDispatch()
  const log = useSelector(state => state.log)
  const token = useSelector(state => state.auth.token)
  const user = useSelector(state => state.user)
  const appTimezone = useSelector(state => state.appConfig.app_timezone) || ''
  const timezone = user.timezone || appTimezone || 'UTC'

  // Max number of actions to be logged in a batch
  const batchSize = 10
  // Max time to wait before sending whatever actions we have (ms)
  const batchInterval = 5 * 60 * 1000 // 5min
  const eventTypes = ['click', 'keydown']
  let keyBuffer = []
  let timer

  const lastLogged = () => {
    const length = log.holding.length
    if (length !== 0) {
      return log.holding[length - 1]
    } else {
      return {}
    }
  }

  const recordLog = useCallback((recordAll = true) => {
    // since we are using the api we can only record if we are
    // logged in so check for an auth token and if we don't have it
    // just leave holding alone until someone does login
    if (token != null) {
      // since we have a token, make a batch and send it
      const { holding } = log
      const batch = recordAll ? holding : holding.splice(0, holding.length - 1)
      if (batch.length > 0) {
        dispatch(xtractAPI('click_log', 'CREATE', batch))
        // If we recorded all, update whats in holding to empty,
        // other wise update it to be whatever we didn't record
        // we could do better here and use the api callback
        // to only remove logs after a confirmed write. Maybe
        // someday.
        dispatch(updateLog(recordAll ? [] : holding))
      }
    }
  }, [token, log.holding])

  const addLogEntry = useCallback((entry, forceSubmit = false) => {
    const { holding } = log
    // see if this entry is a dup of the last entry, if so there is
    // no reason to relog it
    if (
      lastLogged.event !== entry.event ||
      lastLogged.userId !== entry.userId
    ) {
      // Add the new entry
      try {
        if (heapId) {
          heap.track(entry.event, {
            user_id: entry.user_id,
            patient_id: entry.patient_id,
            prescription_id: entry.prescription_id,
            timestamp: entry.timestamp ? new Date(entry.timestamp).toISOString() : Date.now(),
            error: entry.error,
            systemType: getHardwareScreenType()
          });
        }
      } catch (error) {
        console.log(error);
      }
      holding.push(entry)
      dispatch(updateLog(holding))
      // check if a batch is ready to submit
      // Note: batchSize + 1 is so that the last element always
      //  remains in the array so we can use it in getLastLogged
      //  to check for dup entries (eg multiple keypress in
      //  same field)
      if (holding.length > batchSize || forceSubmit) {
        recordLog(forceSubmit)
      }
    }
  }, [log.holding, lastLogged, recordLog, batchSize])

  const onModalSubmitClick = useCallback(() => {
    const { userId, patientId, prescriptionId } = log
    const logEntry = {
      user_id: userId,
      event: '### SUPPORT FLAG ###',
      patient_id: patientId === "undefined" ? null : patientId,
      prescription_id: prescriptionId,
      timestamp: moment().tz(timezone).format('YYYY-MM-DD HH:mm:ss'),
      error: patientId === "undefined"
        ? `Attempted to set patient_id=undefined | ${document.getElementById('support-note').value}`
        : document.getElementById('support-note').value
    }
    addLogEntry(logEntry, true)
    dispatch(closeModal('support_flag_modal'))
  }, [
    log.userId,
    log.patientId,
    log.prescriptionId,
    addLogEntry
  ])

  const doClickLogFlag = useCallback(() => {
    // we only allow them to add a flag if they are actually logged into the app
    if (token != null) {
      dispatch(openModal('support_flag_modal', {
        title: 'Insert support flag',
        content: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            Add a support note about the event you are flagging for the Xtract
            team.
            <textarea
              id="support-note"
              style={{ width: '100%' }}
              aria-label="support flag text input"
            />
            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingTop: '5px',
                width: '60%'
              }}
            >
              <div
                className="btn btn-danger"
                onClick={() => dispatch(closeModal('support_flag_modal'))}
              >
                Cancel
              </div>
              <div
                className="btn btn-success"
                onClick={onModalSubmitClick}
              >
                Submit
              </div>
            </div>
          </div>
        ),
        closeable: true
      }))
    }
  }, [closeModal, token, onModalSubmitClick])

  const checkKeyBuffer = useCallback(() => {
    const knownCommands = [
      {
        command: ['Control', 'Shift', 'X'],
        callback: doClickLogFlag
      }
    ]
    knownCommands.forEach(commandDef => {
      if (_.isEqual(keyBuffer, commandDef.command)) {
        // buffer contains the key combo so execute it
        commandDef.callback()
      }
    })
  }, [keyBuffer])

  const getAria = useCallback(el => {
    let element
    if (el.getAttribute('aria-labelledby') != null) {
      element = el.getAttribute('aria-labelledby')
    } else if (el.getAttribute('aria-label') != null) {
      element = el.getAttribute('aria-label')
    }
    return element
  }, [])

  const accessabilityError = useCallback(error => {
    error.element.classList.add('xtract-accessibility-error')
    error.element.addEventListener(
      'click',
      () => {
        alert(`${error.name}\n\n${error.message}`)
      },
      { once: true }
    )
  }, [])

  const logEvent = useCallback((e, eventType) => {
    // run accessibility scanner while we are here
    if (
      globalConfig.activeLogLevel.toUpperCase() === 'ALL' ||
      globalConfig.activeLogLevel.toUpperCase() === 'ACCESSIBILITY'
    ) {
      scanForProblems(document, accessabilityError)
    }
    let el = e.target
    let element
    if (eventType === 'click') {
      // check element or up to 4 parent elements for aria label
      // this is because sometimes there is a case where a button
      // has an aria label but inside the button is text or an icon
      // if the user clicks the inner elements, without this search
      // nothing would be recorded. 4 parents may be excessive...
      for (let i = 0; i < 6; i++) {
        element = getAria(el)
        if (element != null) {
          break
        }
        el = el.parentElement // prep for next pass
        if (el == null) {
          return // we found nothing and ran out of elements to check
        }
      }
    } else if (eventType === 'keydown') {
      // keep a rolling buffer of the last 3 key presses so we can watch for
      // ctrl + shift + x
      keyBuffer = [...keyBuffer, e.key]
      // only keep the 3 most recent keys
      if (keyBuffer.length > 3) {
        keyBuffer = keyBuffer.slice(1, 4)
      }
      checkKeyBuffer()
    }
    if (element == null) {
      // if we still didn't find a label, record nothing
      return
    }
    const { userId, patientId, prescriptionId } = log
    let updatedPrescriptionId = (el != null && el.dataset != null && el.dataset.prescriptionId != null)
      ? el.dataset.prescriptionId
      : prescriptionId;

    const logEntry = {
      user_id: userId,
      event: `${eventType}: ${element}`,
      patient_id: patientId === "undefined" ? null : patientId,
      prescription_id: updatedPrescriptionId,
      timestamp: moment().tz(timezone).format('YYYY-MM-DD HH:mm:ss'),
      error: patientId === "undefined" ? "Attempted to set patient_id=undefined" : null
    }
    addLogEntry(logEntry)
  }, [
    globalConfig.activeLogLevel,
    scanForProblems,
    accessabilityError,
    keyBuffer,
    checkKeyBuffer,
    log.userId,
    log.patientId,
    log.prescriptionId,
    addLogEntry
  ])

  // this function is regularaly updating based on the most current info but the eventListener
  // wont update regularly so we are passing a ref to the eventListener so that we can keep the
  // ref current without constantly having to update the eventListener.
  const logEventRef = useRef(0)
  useEffect(() => {
    logEventRef.current = logEvent
  }, [logEvent])

  useEffect(() => {
    // componentDidMount

    // start interval timer for sending partial batches
    timer = setInterval(recordLog, batchInterval)
    // add initial event listeners
    eventTypes.forEach(eventType => {
      document.addEventListener(
        eventType,
        e => logEventRef.current(e, eventType),
        true
      )
    })

    return () => {
      // componentWillUnmount
      // record whatever is left in the clickLog
      recordLog(true)
      // cancel the timer
      clearInterval(timer)
      // cancel the eventListeners
      eventTypes.forEach(eventType => {
        document.removeEventListener(eventType, e => logEventRef.current(e, eventType))
      })
    }
  }, [])

  return null
}

export default XtractLogger
